import Stepper from '@codegouvfr/react-dsfr/Stepper';
import { useCurrentCampagne } from "@common/hooks/campagne";
import { useSessionContext } from '@common/hooks';
import { useOfaDetails } from '@common/hooks/organisme';
import { EditOfaInfoStep1FormResult, EditOfaInfoStep2FormResult } from '@common/schemas/forms/organisme';
import {
  CampagneProjection,
  EtablissementProjection,
  OrganismeDto,
  OrganismeStatutEtapier,
  UpdateOrganismeArgs,
  useAddOrganismeEtablissementMutation,
  useGetAllOrganismeEtablissementsQuery,
  useGetCampagneByAnneeQuery,
  useGetOrganismeForCampagneQuery, useGetOrganismeQuery,
  useUpdateOrganismeMutation,
} from '@common/services';
import React, { ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { StepperInfo } from '../../../../types/stepper-info.type';
import { EditOfaInfoStep1 } from './EditOfaInfoStep1';
import { EditOfaInfoStep2 } from './EditOfaInfoStep2';
import { EditOfaInfoStep3 } from "./EditOfaInfoStep3";

export const StepperPage = () => {

  const navigate = useNavigate();

  const [stepperInfo, setStepperInfo] = useState<StepperInfo>({
    currentStep: 1,
    title: 'Données complémentaires',
    nextTitle: "Activité de l'organisme",
  });
  const [step1FormResult, setStep1FormResult] = useState<EditOfaInfoStep1FormResult>(null);
  const [showStep3, setShowStep3] = useState<boolean | null>(null);
  const [step1HasChanged, setStep1HasChanged] = useState<boolean>(false);

  const [updateOrganisme] = useUpdateOrganismeMutation();
  const [addOrganismeEtablissement] = useAddOrganismeEtablissementMutation();

  const { organismeId } = useSessionContext();
  const { organisme, isLoading } = useOfaDetails({ id: organismeId! });

  const { campagne } = useCurrentCampagne();

  const {
    data: campagnePrecedente,
  } = useGetCampagneByAnneeQuery(
      { year: (campagne?.annee ?? 0) - 1, projection: CampagneProjection.ANNEE },
      { skip: !campagne?.annee },
  );

  const {
    data: organismeInscriptionId,
    isLoading: isLoadingOrganismePrecedent,
  } = useGetOrganismeForCampagneQuery(
      { siren: organisme?.siren, campagneId: campagnePrecedente?.id },
      { skip: !campagnePrecedente?.id || !organisme?.siren },
  );

  const {
    data: organismePrecedent,
  } = useGetOrganismeQuery(
      { id: organismeInscriptionId?.id },
      { skip: !organismeInscriptionId?.id }
  )

  const { data: etablissementsPrecedents = [] } = useGetAllOrganismeEtablissementsQuery(
      { id: organismePrecedent?.id, projection: EtablissementProjection.INFO },
      { skip: !organismePrecedent?.id },
  );

  const hasParticipatedPreviousCampagne = !!campagnePrecedente
      && !!organismePrecedent
      && organisme?.statutEtapier === OrganismeStatutEtapier.ETAPE_1;

  if (showStep3 === null && !isLoading && !isLoadingOrganismePrecedent) {
    setShowStep3(hasParticipatedPreviousCampagne);
  }

  const totalSteps = showStep3 ? 3 : 2;

  const STEP_INFO_MAP: Record<number, StepperInfo> = {
    1: { currentStep: 1, title: 'Données complémentaires', nextTitle: 'Activité de l\'organisme' },
    2: {
      currentStep: 2,
      title: "Activité de l'organisme",
      nextTitle: showStep3 ? 'Ajout des établissements' : undefined,
    },
    3: { currentStep: 3, title: 'Ajout des établissements'},
  };

  const scrollToTheTop = () => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  };

  const handleSubmitStep1 = (editOfaInfoStep1FormResult: EditOfaInfoStep1FormResult, hasChanged: boolean) => {
    setStep1FormResult(editOfaInfoStep1FormResult);
    setStep1HasChanged(hasChanged);
    setStepperInfo(STEP_INFO_MAP[2]);
    scrollToTheTop();
  };

  const handleSubmitStep2 = async (editOfaInfoStep2FormResult: EditOfaInfoStep2FormResult) => {
    const updatedOrganisme = {
      ...step1FormResult,
      effectifApprenti: organisme.effectifApprenti,
      avaitActiviteApprentissage: (editOfaInfoStep2FormResult.avaitActiviteApprentissage === 'true'),
      horsLesMurs: (editOfaInfoStep2FormResult.horsLesMurs === 'true'),
      dateDebutActivite: new Date(editOfaInfoStep2FormResult.dateDebutActivite),
      dateDebutExerciceComptable: new Date(editOfaInfoStep2FormResult.dateDebutExerciceComptable),
      dateFinExerciceComptable: new Date(editOfaInfoStep2FormResult.dateFinExerciceComptable),
      partActiviteApprentissage: editOfaInfoStep2FormResult.partActiviteApprentissage,
      groupe: (editOfaInfoStep2FormResult.hasGroupe === 'true') ? editOfaInfoStep2FormResult.groupe : '',
      reseau: (editOfaInfoStep2FormResult.hasReseau === 'true') ? editOfaInfoStep2FormResult.reseau : '',
      cfa: (editOfaInfoStep2FormResult.cfa === 'true'),
      sousTraitant: (editOfaInfoStep2FormResult.sousTraitant === 'true'),
      // On débloque l'étape suivante si on est à l'étape 1
      statutEtapier: (organisme.statutEtapier > OrganismeStatutEtapier.ETAPE_1) ? null : OrganismeStatutEtapier.ETAPE_2,
    } as OrganismeDto;

    await updateOrganisme({ id: organisme.id, dto: updatedOrganisme } as UpdateOrganismeArgs);

    if (showStep3) {
      await Promise.all(
          etablissementsPrecedents.map((etab) =>
            addOrganismeEtablissement({
              id: organisme.id,
              etablissementBody: {
                principal: etab.principal,
                siret: etab.siret,
                denominationUsuelle: etab.denominationUsuelle,
                nda: etab.nda,
                uai: etab.uai,
                effectifApprenti: etab.effectifApprenti,
                adresse: etab.adresse,
                complementAdresse: etab.complementAdresse,
                codePostal: etab.codePostal,
                ville: etab.ville,
                typeDeclaration: etab.typeDeclaration,
              },
            })
          )
      );
      setStepperInfo(STEP_INFO_MAP[3]);
      scrollToTheTop();
    } else {
      navigate('/');
    }
  };

  const handleSubmitStep3 = () => {
    navigate('/');
  }

  const handleStep1Rollback = () => {
    setStepperInfo(STEP_INFO_MAP[1]);
    scrollToTheTop();
  };

  if (isLoading || isLoadingOrganismePrecedent) return null;

  return (
    <div>
      <Stepper
        currentStep={stepperInfo.currentStep}
        nextTitle={stepperInfo.nextTitle as ReactNode | undefined}
        stepCount={totalSteps}
        title={stepperInfo.title}
      />
      <div className={'separator'} />

      <div className={stepperInfo.currentStep === 1 ? '' : 'hidden'}>
        <EditOfaInfoStep1 organisme={organisme} organismePrecedent={showStep3 ? organismePrecedent : null} handleSubmit={handleSubmitStep1} />
      </div>
      <div className={stepperInfo.currentStep === 2 ? '' : 'hidden'}>
        <EditOfaInfoStep2 organisme={organisme} organismePrecedent={showStep3 ? organismePrecedent : null} campagne={campagne} step1HasChanged={step1HasChanged} handleSubmit={handleSubmitStep2} clickPrecedent={handleStep1Rollback} />
      </div>
      {showStep3 && (
        <div className={stepperInfo.currentStep === 3 ? '' : 'hidden'}>
          <EditOfaInfoStep3 nbEtablissements={etablissementsPrecedents.length} anneeCampagnePrecedente={campagnePrecedente.annee} handleSubmit={handleSubmitStep3} />
        </div>
      )}
    </div>
  );
};